import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';

import { BehaviorSubject, tap } from 'rxjs';
import { environment } from '../../environments/environment';
import { StateCleanupService } from './state-cleanup.service';

export interface Course {
  id: string;
  title: string;
  items: string[];
  description: string;
  createdAt: string;
  updatedAt: string;
  lessons: string[];
  coverImage: {
    media: {
      image: {
        src: string;
      };
    };
  };
}

export interface Lession {
  id: string;
  title: string;
  items: string[];
  createdAt: string;
  updatedAt: string;
  position: number;
}

export interface Item {
  id: string;
  type: string;
  name: string;
  items: SingleItem[];
  variant?: string;
}

export interface SingleItem {
  id: string;
  heading?: string;
  paragraph?: string;
}

@Injectable({
  providedIn: 'root',
})
export class CourseService {
  courses$ = new BehaviorSubject<Course[]>([]);
  lessions$ = new BehaviorSubject<Lession[]>([]);
  items$ = new BehaviorSubject<Item[]>([]);

  constructor(
    protected http: HttpClient,
    private router: Router,
    private stateCleanupService: StateCleanupService
  ) {
    this.stateCleanupService.register(() => this.resetState());
  }

  listAllCourses() {
    return this.http.get<Course[]>(`${environment.coursesBackend}/courses`).pipe(
      tap((courses) => {
        this.courses$.next(courses);
      })
    );
  }

  listCourses(ids: string[]) {
    return this.http
      .post<Course[]>(`${environment.coursesBackend}/search/ids`, {
        index: 'courses',
        ids,
      })
      .pipe(
        tap((courses) => {
          this.courses$.next(courses);
        })
      );
  }

  listLessons(ids: string[]) {
    return this.http
      .post<Lession[]>(`${environment.coursesBackend}/search/ids`, {
        index: 'lessons',
        ids,
      })
      .pipe(
        tap((lessons) => {
          lessons = lessons.sort((a, b) => a.position - b.position);
          this.lessions$.next(lessons);
        })
      );
  }

  listItems(ids: string[]) {
    return this.http
      .post<Item[]>(`${environment.coursesBackend}/search/ids`, {
        index: 'items',
        ids,
      })
      .pipe(
        tap((items) => {
          this.items$.next(items);
        })
      );
  }

  private resetState() {
    this.courses$.next([]);
    this.lessions$.next([]);
    this.items$.next([]);
  }
}
